import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { MessageCircle, Phone } from 'lucide-react';

/**
 * Motion layer for the plans page — one calm "director" plus a signature effect per plan.
 *
 * Idle:  Basic → (gap) → Standard → (gap) → Pro → (gap) → … — one card at a time, never together.
 * Hover: the cycle stops, the hovered card plays its signature AND a hover-only extra, plus a 3D
 *        tilt with a cursor-following sheen. Leaving resumes the cycle from the next card.
 * Pro's star field twinkles all the time (it is the card's "night" texture, not an event).
 * Off-screen / hidden tab / prefers-reduced-motion: the cycle doesn't run.
 */

export type FxMode = 'cycle' | 'hover';
export interface FxState { key: string; mode: FxMode; run: number }

const GAP_MS = 2000;
const FIRST_DELAY_MS = 1400;
const RESUME_MS = 900;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function useFxDirector(order: string[], durations: Record<string, number>, ready: boolean) {
  const [active, setActive] = useState<FxState | null>(null);
  const timer = useRef<number | undefined>();
  const idx = useRef(0);
  const run = useRef(0);
  const hovered = useRef<string | null>(null);
  const visible = useRef(true);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const orderKey = order.join('|');

  const clear = () => { if (timer.current) window.clearTimeout(timer.current); timer.current = undefined; };

  const schedule = useCallback((delay: number) => {
    clear();
    if (prefersReducedMotion() || hovered.current || !visible.current || document.hidden || !order.length) return;
    timer.current = window.setTimeout(() => {
      const key = order[idx.current % order.length];
      run.current += 1;
      setActive({ key, mode: 'cycle', run: run.current });
      timer.current = window.setTimeout(() => {
        setActive(null);
        idx.current = (idx.current + 1) % order.length;
        schedule(GAP_MS);
      }, durations[key] ?? 2000);
    }, delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey]);

  // Starts only once the cards are actually on screen (so the observer has a node and the first
  // beat isn't spent behind the loading skeleton) — and always with the first plan (Basic).
  useEffect(() => {
    if (!ready) return undefined;
    idx.current = 0;
    schedule(FIRST_DELAY_MS);
    const onVis = () => (document.hidden ? clear() : schedule(GAP_MS));
    document.addEventListener('visibilitychange', onVis);
    let io: IntersectionObserver | undefined;
    if (rootRef.current && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(([e]) => {
        visible.current = e.isIntersecting;
        if (e.isIntersecting) schedule(GAP_MS / 2); else clear();
      }, { threshold: 0.25 });
      io.observe(rootRef.current);
    }
    return () => { clear(); document.removeEventListener('visibilitychange', onVis); io?.disconnect(); };
  }, [schedule, ready]);

  const enter = useCallback((key: string) => {
    clear();
    hovered.current = key;
    run.current += 1;
    setActive({ key, mode: 'hover', run: run.current });
  }, []);

  const leave = useCallback((key: string) => {
    if (hovered.current !== key) return;
    hovered.current = null;
    setActive(null);
    idx.current = (order.indexOf(key) + 1) % Math.max(1, order.length);
    schedule(RESUME_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey, schedule]);

  const stateFor = (key: string) => (active && active.key === key ? active : null);
  return { rootRef, stateFor, enter, leave };
}

/* ------------------------------------------------------------------------------------------------
 * Card shell: hover tilt toward the cursor + a sheen that follows it. Pure DOM writes per move
 * (no React state), rAF-throttled; touch / reduced-motion devices get neither.
 * --------------------------------------------------------------------------------------------- */
export const FxCard: React.FC<{
  fxKey: string;
  onEnter: (k: string) => void;
  onLeave: (k: string) => void;
  sheen: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  'data-testid'?: string;
}> = ({ fxKey, onEnter, onLeave, sheen, className, style, children, ...rest }) => {
  const ref = useRef<HTMLElement | null>(null);
  const frame = useRef<number>();
  const canTilt = () => !prefersReducedMotion() && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || !canTilt()) return;
    const { clientX, clientY } = e;
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      const x = (clientX - r.left) / r.width;
      const y = (clientY - r.top) / r.height;
      el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
      el.style.transform = `perspective(1100px) rotateX(${((0.5 - y) * 5).toFixed(2)}deg) rotateY(${((x - 0.5) * 6).toFixed(2)}deg) translateY(-6px)`;
    });
  };
  const reset = () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    if (ref.current) ref.current.style.transform = '';
  };

  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      className={`psfx-card ${className ?? ''}`}
      style={{ ...style, ['--sheen' as string]: sheen }}
      onMouseEnter={() => onEnter(fxKey)}
      onMouseLeave={() => { reset(); onLeave(fxKey); }}
      onMouseMove={onMove}
      {...rest}
    >
      <span aria-hidden className="psfx-sheen" />
      {children}
    </article>
  );
};

/* ------------------------------------------------------------------------------------------------
 * PRO: a living night sky. Always-on twinkle; the cycle beat (and hover) sends one shooting star
 * from above the card down to the right along a smooth arc. Hover adds an aurora + faster twinkle.
 * --------------------------------------------------------------------------------------------- */
const STARS = Array.from({ length: 28 }, (_, i) => {
  const r = (n: number) => ((Math.sin(i * 127.1 + n * 311.7) * 43758.5453) % 1 + 1) % 1; // deterministic
  return { x: r(1) * 100, y: r(2) * 62, s: r(3) > 0.82 ? 2 : r(3) > 0.45 ? 1.5 : 1, d: 2.4 + r(4) * 3.2, delay: -r(5) * 5 };
});

/** The meteor's arc inside the Pro card: launches sideways from the top-left, then gravity bends it
 *  down towards the right edge. Shared by the head (offset-path) and the trail (SVG path). */
const METEOR_PATH = 'M 58 30 Q 262 4 332 292';

export const ProSky: React.FC<{ fx: FxState | null }> = ({ fx }) => {
  const hover = fx?.mode === 'hover';
  return (
    <span aria-hidden className={`psfx-sky ${hover ? 'is-hover' : ''}`}>
      {STARS.map((st, i) => (
        <i key={i} className="psfx-star" style={{ left: `${st.x}%`, top: `${st.y}%`, width: st.s, height: st.s, ['--d' as string]: `${st.d}s`, animationDelay: `${st.delay}s` }} />
      ))}
      {hover && <span key={`aurora-${fx?.run}`} className="psfx-aurora" />}
      {fx && (
        // The trail is the arc itself (three layered dashes riding the same curve, synced to the
        // head) so it bends like a real meteor instead of a straight stick sliding along.
        <span key={`shoot-${fx.run}`} className="psfx-shoots" style={{ ['--sd' as string]: hover ? '.3s' : '0s' }}>
          <svg className="psfx-trail" width="100%" height="100%" aria-hidden>
            <defs>
              <filter id={`glow-${fx.run}`} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="1.6" /></filter>
            </defs>
            <path className="psfx-tr psfx-tr-1" d={METEOR_PATH} pathLength={100} filter={`url(#glow-${fx.run})`} />
            <path className="psfx-tr psfx-tr-2" d={METEOR_PATH} pathLength={100} />
            <path className="psfx-tr psfx-tr-3" d={METEOR_PATH} pathLength={100} />
          </svg>
          <span className="psfx-head"><i /></span>
        </span>
      )}
    </span>
  );
};

/* ------------------------------------------------------------------------------------------------
 * STANDARD: a real marker stroke under each line of the one thing only this tier has. One smooth,
 * gently arched brush shape (thick body, tapered ends, a light dry-brush texture), so there is no
 * hand jitter; it is revealed left to right like a marker gliding. Measured from the rendered text
 * per visual line (mixed-direction fragments merged). Hover keeps it and adds a soft highlighter.
 * --------------------------------------------------------------------------------------------- */
interface LineBox { x: number; y: number; w: number; h: number }

/** Closed outline of a tapered, gently arched marker stroke, `w` px long inside an `h` px box. */
function brushOutline(w: number, h: number, weight: number, lift: number): string {
  const n = 32;
  const top: string[] = [];
  const bottom: string[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const x = 2 + t * (w - 4);
    // a single smooth arc: dips a touch in the middle and rises towards the end, like a wrist flick
    const y = h * 0.62 - Math.sin(Math.PI * t) * lift;
    const half = (0.9 + weight * Math.pow(Math.sin(Math.PI * Math.min(1, t * 1.06 + 0.02)), 0.55)) / 2;
    top.push(`${x.toFixed(1)},${(y - half).toFixed(2)}`);
    bottom.unshift(`${x.toFixed(1)},${(y + half).toFixed(2)}`);
  }
  return `M${top.join(' L')} L${bottom.join(' L')} Z`;
}

const BrushStroke: React.FC<{ w: number; delay: number; hold: boolean; seed: number }> = ({ w, delay, hold, seed }) => {
  const id = useId().replace(/:/g, '');
  const h = 16;
  return (
    <svg className={`psfx-brush ${hold ? 'hold' : ''}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ ['--delay' as string]: `${delay}s` }} aria-hidden>
      <defs>
        <filter id={`tx-${id}`} x="-4%" y="-60%" width="108%" height="220%">
          <feTurbulence type="fractalNoise" baseFrequency="0.035 0.75" numOctaves={2} seed={seed} result="n" />
          <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -1.15 1.42" result="a" />
          <feComposite in="SourceGraphic" in2="a" operator="in" />
        </filter>
        <mask id={`rv-${id}`} maskUnits="userSpaceOnUse">
          <path className="psfx-reveal" d={`M-6 ${h / 2} L${w + 6} ${h / 2}`} stroke="#fff" strokeWidth={h * 3} pathLength={1} />
        </mask>
      </defs>
      <g mask={`url(#rv-${id})`} filter={`url(#tx-${id})`}>
        <path className="psfx-ink" d={brushOutline(w, h, 7.5, 2.4)} />
        <path className="psfx-ink second" d={brushOutline(w * 0.92, h, 3, 2)} transform={`translate(${w * 0.06} 1.4)`} />
      </g>
    </svg>
  );
};

export const MarkerHighlight: React.FC<{ fx: FxState | null; children: React.ReactNode }> = ({ fx, children }) => {
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [lines, setLines] = useState<LineBox[]>([]);
  const hover = fx?.mode === 'hover';

  // Measured per beat and again whenever the text box changes (resize, language switch).
  useLayoutEffect(() => {
    if (!fx || !wrapRef.current || !textRef.current) return undefined;
    const wrap = wrapRef.current;
    const text = textRef.current;
    const measure = () => {
      const base = wrap.getBoundingClientRect();
      const merged: LineBox[] = [];
      Array.from(text.getClientRects()).filter(r => r.width > 2).forEach(r => {
        const box = { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
        const same = merged.find(m => Math.abs(m.y - box.y) < box.h / 2);
        if (!same) { merged.push(box); return; }
        const left = Math.min(same.x, box.x);
        same.w = Math.max(same.x + same.w, box.x + box.w) - left;
        same.x = left;
      });
      setLines(merged.sort((a, b) => a.y - b.y));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    ro?.observe(wrap);
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx?.run, children]);

  return (
    <span ref={wrapRef} className="psfx-mark">
      <span ref={textRef}>{children}</span>
      {fx && lines.map((ln, i) => (
        <React.Fragment key={`${fx.run}-${i}`}>
          {hover && (
            <span className="psfx-hl" style={{ left: ln.x - 3, top: ln.y + ln.h * 0.16, width: ln.w + 6, height: ln.h * 0.74, animationDelay: `${0.45 + i * 0.35}s` }} />
          )}
          <span className="psfx-brush-wrap" style={{ left: ln.x - 4, top: ln.y + ln.h - 5 }}>
            <BrushStroke w={Math.round(ln.w + 8)} delay={i * 0.42} hold={hover} seed={3 + i} />
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

/* ------------------------------------------------------------------------------------------------
 * BASIC: "schedule without limits" as a small fountain of message bubbles: they pop up from one
 * point, arc out and fade. Gentle on the cycle beat; on hover a little livelier (denser, higher),
 * still delicate. Bubbles are spawned imperatively with the Web Animations API, so nothing
 * re-renders per particle.
 * --------------------------------------------------------------------------------------------- */
const MSG_EMOJI = ['👋', '📅', '🎉', '⏰', '📦', '✅', '🙏', '💬', '🔥', '🎁'];
const TICKS_SVG = '<svg viewBox="0 0 16 11" width="16" height="11" aria-hidden="true"><path d="M1.2 5.9l2.6 2.6L9.6 2.2M6.8 8.1l.6.6 5.8-6.4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** One WhatsApp outgoing message: green bubble with a tail, an emoji, the send time, blue ticks. */
const messageEl = (n: number) => {
  const el = document.createElement('span');
  el.className = 'psfx-msg';
  el.innerHTML = `<span class="psfx-msg-emoji">${MSG_EMOJI[n % MSG_EMOJI.length]}</span><span class="psfx-msg-meta">9:0${n % 10}<span class="psfx-msg-ticks">${TICKS_SVG}</span></span>`;
  return el;
};

export const MessageFountain: React.FC<{ fx: FxState | null }> = ({ fx }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const mode = fx?.mode;

  useEffect(() => {
    const host = ref.current;
    if (!fx || !host || prefersReducedMotion() || typeof host.animate !== 'function') return undefined;
    const hover = mode === 'hover';
    const every = hover ? 210 : 330;
    const stopAfter = hover ? Infinity : 2300;
    const started = performance.now();
    let n = Math.floor(Math.random() * 10);

    const spawn = () => {
      if (performance.now() - started > stopAfter) return;
      n += 1;
      const el = messageEl(n);
      host.appendChild(el);
      // alternate sides so the fountain fans out evenly instead of clumping
      const side = n % 2 ? 1 : -1;
      const spread = side * ((hover ? 34 : 26) + Math.random() * (hover ? 78 : 62));
      const rise = (hover ? 54 : 46) + Math.random() * (hover ? 18 : 12);
      const tilt = side * (3 + Math.random() * 6);
      const frames: Keyframe[] = [];
      for (let k = 0; k <= 12; k++) {
        const t = k / 12;
        const x = spread * t;
        const y = -rise * (2 * t - 1.05 * t * t); // fountain arc: shoots up, eases over, drifts down a touch
        const s = t < 0.16 ? 0.35 + (t / 0.16) * 0.7 : t < 0.26 ? 1.05 - ((t - 0.16) / 0.1) * 0.05 : 1 - (t - 0.26) * 0.12;
        const o = t < 0.1 ? t / 0.1 : t > 0.62 ? Math.max(0, 1 - (t - 0.62) / 0.38) : 1;
        frames.push({ transform: `translate(-50%, -50%) translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) rotate(${(tilt * t).toFixed(1)}deg) scale(${s.toFixed(3)})`, opacity: o });
      }
      const anim = el.animate(frames, { duration: (hover ? 1600 : 1850) + Math.random() * 250, easing: 'cubic-bezier(.22,.61,.36,1)' });
      anim.onfinish = () => el.remove();
      const btn = host.parentElement?.querySelector('.psfx-send-btn');
      btn?.classList.remove('is-send');
      void (btn as HTMLElement | null)?.offsetWidth; // restart the send pulse
      btn?.classList.add('is-send');
    };

    let timer: number | undefined;
    const lead = window.setTimeout(() => {
      spawn();
      timer = window.setInterval(spawn, every);
    }, 380);
    return () => { window.clearTimeout(lead); if (timer) window.clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx?.run, mode]);

  return (
    <div aria-hidden className={`psfx-fountain ${fx ? 'is-live' : ''}`}>
      <div ref={ref} className="psfx-emitter" />
      <span className="psfx-send"><span className="psfx-send-btn">
        <MessageCircle size={24} strokeWidth={2.1} color="#fff" />
        <Phone size={10} strokeWidth={0} fill="#fff" className="psfx-send-phone" />
      </span></span>
    </div>
  );
};

/** Durations of each plan's cycle beat (ms) — what the director waits before the 2s gap. */
export const FX_DURATIONS: Record<string, number> = { Scheduler: 3000, Manager: 2600, Campaigner: 3500 };

export const FX_STYLES = `
.psfx-card{position:relative;isolation:isolate;transform-style:preserve-3d;transition:transform .35s cubic-bezier(.2,.8,.2,1),box-shadow .35s ease;will-change:transform}
.psfx-card:hover{box-shadow:0 34px 60px -34px rgba(15,23,42,.55)!important}
.psfx-sheen{position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:0;transition:opacity .35s ease;z-index:-1;background:radial-gradient(420px circle at var(--mx,50%) var(--my,0%),var(--sheen),transparent 60%)}
.psfx-card:hover .psfx-sheen{opacity:1}

/* PRO sky */
.psfx-sky{position:absolute;inset:0;z-index:-1;pointer-events:none;overflow:hidden;border-radius:inherit;--speed:1}
.psfx-sky.is-hover{--speed:.45}
.psfx-star{position:absolute;border-radius:50%;background:#fff;box-shadow:0 0 4px rgba(255,255,255,.8);opacity:.25;animation:psfx-twinkle calc(var(--d) * var(--speed)) ease-in-out infinite}
@keyframes psfx-twinkle{0%,100%{opacity:.18;transform:scale(.7)}50%{opacity:.95;transform:scale(1.15)}}
.psfx-shoots{position:absolute;inset:0}
.psfx-trail{position:absolute;inset:0;overflow:visible}
.psfx-tr{fill:none;stroke-linecap:round;animation-duration:3.2s;animation-timing-function:cubic-bezier(.42,0,.72,.98);animation-delay:var(--sd,0s);animation-fill-mode:both}
.psfx-tr-1{stroke:rgba(125,211,252,.55);stroke-width:5;stroke-dasharray:20 220;animation-name:psfx-trail-1}
.psfx-tr-2{stroke:rgba(224,242,254,.75);stroke-width:2;stroke-dasharray:13 220;animation-name:psfx-trail-2}
.psfx-tr-3{stroke:#fff;stroke-width:1.2;stroke-dasharray:5 220;animation-name:psfx-trail-3}
@keyframes psfx-trail-1{0%{stroke-dashoffset:20;opacity:0}10%{opacity:1}74%{opacity:.9}100%{stroke-dashoffset:-80;opacity:0}}
@keyframes psfx-trail-2{0%{stroke-dashoffset:13;opacity:0}10%{opacity:1}74%{opacity:.9}100%{stroke-dashoffset:-87;opacity:0}}
@keyframes psfx-trail-3{0%{stroke-dashoffset:5;opacity:0}10%{opacity:1}74%{opacity:.9}100%{stroke-dashoffset:-95;opacity:0}}
.psfx-head{position:absolute;left:0;top:0;width:9px;height:9px;margin:0;offset-path:path("M 58 30 Q 262 4 332 292");offset-rotate:0deg;offset-anchor:50% 50%;offset-distance:0%;border-radius:50%;background:radial-gradient(circle,#fff 0 35%,rgba(224,242,254,.9) 55%,rgba(125,211,252,0) 72%);box-shadow:0 0 12px 4px rgba(125,211,252,.55),0 0 26px 8px rgba(56,189,248,.25);animation:psfx-head 3.2s cubic-bezier(.42,0,.72,.98) var(--sd,0s) both}
.psfx-head i{position:absolute;left:50%;top:50%;width:22px;height:22px;margin:-11px 0 0 -11px;background:linear-gradient(90deg,transparent 42%,rgba(255,255,255,.9) 50%,transparent 58%),linear-gradient(0deg,transparent 42%,rgba(255,255,255,.9) 50%,transparent 58%);-webkit-mask:radial-gradient(circle,#000 20%,transparent 68%);mask:radial-gradient(circle,#000 20%,transparent 68%);animation:psfx-sparkle 1.1s linear infinite}
@keyframes psfx-head{0%{offset-distance:0%;opacity:0;transform:scale(.6)}10%{opacity:1;transform:scale(1)}74%{opacity:.95}100%{offset-distance:100%;opacity:0;transform:scale(.7)}}
@keyframes psfx-sparkle{to{transform:rotate(90deg)}}
[dir=rtl] .psfx-shoots{transform:scaleX(-1)}
.psfx-aurora{position:absolute;left:-20%;right:-20%;top:-30%;height:70%;filter:blur(28px);opacity:0;background:linear-gradient(100deg,transparent 10%,rgba(45,212,191,.42) 35%,rgba(56,189,248,.38) 55%,rgba(167,139,250,.32) 72%,transparent 90%);animation:psfx-aurora-in .9s ease-out forwards,psfx-aurora-drift 7s ease-in-out .9s infinite alternate}
@keyframes psfx-aurora-in{to{opacity:.9}}
@keyframes psfx-aurora-drift{from{transform:translateX(-6%) skewY(-4deg)}to{transform:translateX(6%) skewY(3deg)}}

/* STANDARD: marker brush */
.psfx-mark{position:relative;display:inline-block}
.psfx-brush-wrap{position:absolute;line-height:0;pointer-events:none}
.psfx-brush{overflow:visible;display:block}
.psfx-ink{fill:#e5262f;opacity:.92}
.psfx-ink.second{opacity:.5}
.dark .psfx-ink{fill:#f43f5e}
.psfx-reveal{fill:none;stroke-dasharray:1;stroke-dashoffset:1;animation:psfx-reveal .75s cubic-bezier(.55,.05,.25,1) var(--delay,0s) forwards}
.psfx-brush{animation:psfx-brush-out 2.6s linear var(--delay,0s) forwards}
.psfx-brush.hold{animation:none}
@keyframes psfx-reveal{to{stroke-dashoffset:0}}
@keyframes psfx-brush-out{0%,78%{opacity:1}100%{opacity:0}}
.psfx-hl{position:absolute;z-index:-1;border-radius:4px 7px 5px 6px;background:color-mix(in srgb,#fde68a 62%,transparent);transform-origin:left center;transform:scaleX(0) skewX(-6deg);animation:psfx-hl .55s cubic-bezier(.3,.7,.3,1) forwards}
[dir=rtl] .psfx-hl{transform-origin:right center}
.dark .psfx-hl{background:color-mix(in srgb,#f59e0b 22%,transparent)}
@keyframes psfx-hl{to{transform:scaleX(1) skewX(-6deg)}}
[dir=rtl] .psfx-brush{transform:scaleX(-1)}

/* BASIC: a fountain of WhatsApp messages from a green "send" button */
.psfx-fountain{position:relative;height:86px;margin-top:8px;pointer-events:none}
.psfx-emitter{position:absolute;left:50%;bottom:26px;width:0;height:0;z-index:2}
.psfx-send{position:absolute;left:50%;bottom:0;width:42px;height:42px;margin-left:-21px;z-index:3;opacity:0;transform:translateY(16px) scale(.85);transition:opacity .45s ease,transform .55s cubic-bezier(.2,1.2,.4,1)}
.psfx-fountain.is-live .psfx-send{opacity:1;transform:none}
.psfx-send-btn{position:relative;display:flex;align-items:center;justify-content:center;width:100%;height:100%;border-radius:50%;background:#25d366;box-shadow:0 8px 18px -8px rgba(37,211,102,.85)}
.psfx-send-phone{position:absolute;left:50%;top:50%;transform:translate(-50%,-52%)}
.psfx-send-btn.is-send{animation:psfx-send .45s cubic-bezier(.3,1.6,.5,1)}
.psfx-send-btn.is-send::after{content:"";position:absolute;inset:0;border-radius:50%;border:2px solid #25d366;animation:psfx-ring .7s ease-out forwards}
@keyframes psfx-send{0%{transform:scale(1)}40%{transform:scale(.88)}100%{transform:scale(1)}}
@keyframes psfx-ring{from{transform:scale(1);opacity:.7}to{transform:scale(1.9);opacity:0}}
.psfx-msg{position:absolute;left:0;top:0;opacity:0;display:inline-flex;align-items:flex-end;gap:5px;padding:4px 7px 3px 8px;border-radius:8px 8px 2px 8px;background:#d9fdd3;color:#111b21;font-size:15px;line-height:1;white-space:nowrap;box-shadow:0 1px .5px rgba(11,20,26,.13),0 6px 14px -8px rgba(11,20,26,.35);will-change:transform,opacity}
.psfx-msg::after{content:"";position:absolute;right:-5px;bottom:0;border-style:solid;border-width:0 0 7px 7px;border-color:transparent transparent transparent #d9fdd3}
.psfx-msg-emoji{font-size:15px;line-height:1}
.psfx-msg-meta{display:inline-flex;align-items:center;gap:2px;font-size:9.5px;color:#667781;font-variant-numeric:tabular-nums;transform:translateY(1px)}
.psfx-msg-ticks{display:inline-flex;color:#53bdeb}
.dark .psfx-msg{background:#005c4b;color:#e9edef}
.dark .psfx-msg::after{border-color:transparent transparent transparent #005c4b}
.dark .psfx-msg-meta{color:#a5c9c0}

/* Pro schematic reacts on hover */
.psfx-card:hover .psv2-flow{animation-duration:.45s!important}

@media (prefers-reduced-motion:reduce){
  .psfx-card{transition:none}
  .psfx-star,.psfx-tr,.psfx-head,.psfx-aurora,.psfx-reveal,.psfx-brush,.psfx-hl,.psfx-send-btn,.psfx-send-btn::after{animation:none!important}
  .psfx-reveal{stroke-dashoffset:0}
  .psfx-hl{transform:none}
  .psfx-shoots,.psfx-aurora{display:none}
}
`;
